import { Box, DrawerCloseButton } from '@chakra-ui/react';
import classNames from 'classnames';

import { coerceBoolean, isDefined } from '@enterprise/core';

import { DefaultToolTip } from '#tool-tip/default-tool-tip.tsx';

import type { HeaderProps } from './types/header.props';

/**
 * Renders the header section for a slide-out drawer component.
 *
 * @param {HeaderProps} props - The properties for the Header component.
 * @param {string} [props.className] - Additional CSS class names to apply to the header.
 * @param {React.ReactNode} [props.controls] - Optional controls to display on the right side of the header.
 * @param {IconProp} [props.icon] - Optional FontAwesome icon to display on the left side of the header.
 * @param {() => void} [props.onClose] - Callback function invoked when the close button is clicked.
 * @param {string | React.ReactNode} props.title - The title to display in the header. If a string,
 * it will be wrapped in a tooltip.
 * @param {boolean | string} [props.tooltip=true] - If `true`, uses the title as the tooltip label.
 * If a string, uses it as the tooltip label. If `false`, disables the tooltip.
 * @returns {JSX.Element} The rendered header component.
 */
export const Header = ({ className, controls, icon, onClose, title, tooltip = true, ...props }: HeaderProps) => {
  const isBoolean = coerceBoolean(tooltip);

  return (
    <Box
      className={classNames('slide-out-header', className)}
      {...props}>
      {isDefined(icon) && <Box className="icon">{icon}</Box>}
      <Box
        flexGrow={1}
        isTruncated>
        {typeof title === 'string' && (
          <DefaultToolTip label={isBoolean ? title : tooltip}>
            <span className="truncate">{title}</span>
          </DefaultToolTip>
        )}
        {typeof title !== 'string' && title}
      </Box>
      {controls}
      <DrawerCloseButton
        className="close-button"
        onClick={onClose}
      />
    </Box>
  );
};
